import React, { useState, useEffect, useRef } from "react";
import { Button, TextField, Typography, Box, Container } from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import { useLocation } from "react-router-dom";
import { styled } from "@mui/system";
import InvoiceHeader from "../InvoiceHeader";
import generatePDF from "../../../tools/generatePDFSummary";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import InvoiceDetails from "./InvoiceSummaryDetails";
import RowsTable from "./RowsSummaryTable";
import CompanyInfo from "../CompanyPymtInfo";
import SpecialNotes from "../SpecialNotes";
import NotesSummary from "./NotesSummaryTable";
import ThankYouForB from "../ThankYouDiv";
import {
  validateSecureInvoiceLink,
  markInvoiceLinkAsUsed,
} from "../../../services/invoiceSecureLinkService";

export default function InvoiceSummary() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const linkId = queryParams.get("linkId");

  const [invoiceData, setInvoiceData] = useState(null);
  const [linkValidation, setLinkValidation] = useState({
    isValid: false,
    isLoading: true,
    status: null,
    message: "",
    linkData: null,
  });

  const [total, setTotal] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [printedName, setPrintedName] = useState("");

  const sigCanvas = useRef(null);

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  useEffect(() => {
    if (invoiceData && Array.isArray(invoiceData.rows)) {
      const newTotal = invoiceData.rows.reduce(
        (acc, row) => acc + (Number(row.price) || 0),
        0
      );
      setTotal(newTotal);
    } else {
      setTotal(0);
    }
  }, [invoiceData]);

  useEffect(() => {
    const validateLink = async () => {
      if (!linkId) {
        setLinkValidation({
          isValid: false,
          isLoading: false,
          status: "not_found",
          message: "No valid link was provided",
          linkData: null,
        });
        return;
      }

      try {
        const validation = await validateSecureInvoiceLink(linkId);

        if (validation.isValid) {
          setLinkValidation({
            isValid: true,
            isLoading: false,
            status: "valid",
            message: validation.message,
            linkData: validation.linkData,
          });

          setInvoiceData(validation.linkData?.invoiceData || null);
        } else {
          setLinkValidation({
            isValid: false,
            isLoading: false,
            status: validation.status,
            message: validation.message,
            linkData: null,
          });
        }
      } catch (error) {
        console.error("Error validating link:", error);
        setLinkValidation({
          isValid: false,
          isLoading: false,
          status: "error",
          message: "Error validating the link",
          linkData: null,
        });
      }
    };

    validateLink();
  }, [linkId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!invoiceData) {
      return;
    }

    if (!printedName.trim()) {
      alert("Please enter the printed name");
      return;
    }

    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      alert("Please provide a signature");
      return;
    }

    try {
      if (linkId) {
        await markInvoiceLinkAsUsed(linkId);
      }

      const signatureDataUrl = sigCanvas.current.toDataURL();

      const invoice = {
        ...invoiceData,
        signature: signatureDataUrl,
        printedName,
      };

      generatePDF(invoice, invoiceData.orderNumber);
      setOpenSnackbar(true);

      clearSignature();
      setPrintedName("");

      setTimeout(() => {
        setLinkValidation({
          isValid: false,
          isLoading: false,
          status: "already_used",
          message: "Form submitted successfully. This link is no longer valid.",
          linkData: null,
        });
        setInvoiceData(null);
      }, 1500);
    } catch (error) {
      console.error("Error submitting invoice:", error);
      alert("Error submitting invoice. Please try again.");
    }
  };

  const StyledButton = styled(Button)({
    fontWeight: "bold",
    backgroundColor: "#000",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#ff0000",
    },
  });

  if (linkValidation.isLoading) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="h6">Validating secure link...</Typography>
        </Box>
      </Container>
    );
  }

  if (!linkValidation.isValid || !invoiceData) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          textAlign="center"
        >
          <Typography variant="h6">{linkValidation.message}</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <InvoiceHeader orderNumber={invoiceData?.orderNumber || ""} />

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <InvoiceDetails invoiceData={invoiceData} />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
            flexDirection: "column",
          }}
        >
          <RowsTable rows={invoiceData?.rows || []} total={total} />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <NotesSummary invoiceData={invoiceData} />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <SpecialNotes />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <CompanyInfo />
        </div>

        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="printedName"
          label="Printed Name"
          name="printedName"
          autoFocus
          value={printedName}
          onChange={(e) => setPrintedName(e.target.value)}
        />

        <Typography
          variant="h6"
          style={{ marginTop: "20px", fontFamily: "Lato, sans-serif" }}
        >
          Signature
        </Typography>

        <SignatureCanvas
          ref={sigCanvas}
          canvasProps={{
            width: 500,
            height: 200,
            className: "signature-canvas",
          }}
          penColor="black"
        />

        <Button
          onClick={clearSignature}
          variant="outlined"
          color="error"
          style={{ marginTop: "10px", marginRight: "10px" }}
        >
          Clear Signature
        </Button>

        <ThankYouForB />

        <StyledButton
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          style={{ margin: "20px 0" }}
        >
          I agree
        </StyledButton>
      </form>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={1500}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Data sent correctly. You can close the window
        </Alert>
      </Snackbar>
    </>
  );
}